import {
  Autocomplete,
  Loader,
  OptionsFilter,
  useComputedColorScheme,
  Text,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { useSearchProviders } from 'api/resource';
import { useIsAdmin } from 'hooks';
import { useCurrentSiteStore } from 'store/SiteStore';
import { Provider } from 'interfaces';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

interface AddProviderToSiteSearchProps {
  siteProviders?: Provider[];
  selectedProviders?: Provider | null;
  setSelectedProviders?: Dispatch<SetStateAction<Provider | null>> | undefined;
  /** Callback when user chooses to add a brand new provider (no search match) */
  onAddNewProvider?: () => void;
}

export const AddProviderToSiteSearch: React.FC<
  AddProviderToSiteSearchProps
> = ({
  siteProviders,
  selectedProviders,
  setSelectedProviders,
  onAddNewProvider,
}) => {
  const isAdmin = useIsAdmin();
  const { name: currentSiteName } = useCurrentSiteStore();
  const [searchValue, setSearchValue] = useState<string>('');
  const [debounced] = useDebouncedValue<string>(searchValue, 500);
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';

  const { data: searchData, isLoading } = useSearchProviders({
    searchValue: debounced,
  });

  const ADD_NEW_VALUE = '__ADD_NEW_PROVIDER__';

  // We use renderOption instead of itemComponent (Autocomplete does not support itemComponent)

  const options = useMemo(() => {
    const base = searchData || [];
    const providerIsLinkedForSite = (provider: Provider) => {
      const siteProvider = siteProviders?.find(
        (siteProv) => siteProv.id === provider.id
      );
      return !!(
        siteProvider?.userID ||
        siteProvider?.user ||
        provider.userID ||
        provider.user
      );
    };
    const providerCanBeSelected = (provider: Provider) => {
      const siteProvider = siteProviders?.find(
        (siteProv) => siteProv.id === provider.id
      );
      if (!siteProvider) return true;
      return !providerIsLinkedForSite(provider);
    };
    const filtered = isAdmin
      ? base.filter(providerCanBeSelected)
      : base.filter(
          (p) => selectedProviders?.id !== p.id && providerCanBeSelected(p)
        );
    const mapped = filtered.map((p) => ({
      value: p.id,
      label: `${p.firstName} ${p.lastName}${
        siteProviders?.some((siteProv) => siteProv.id === p.id)
          ? ' (already in site)'
          : ''
      }`,
    }));
    // Only show the "Add New Provider" option when user is admin and no matches
    if (
      isAdmin &&
      !base.length &&
      !mapped.length &&
      debounced.trim().length > 0
    ) {
      return [
        {
          value: ADD_NEW_VALUE,
          'data-testid': 'add-new-provider',
          label: `Create "${debounced}" and add to ${currentSiteName}`,
          isAddNew: true,
          searchTerm: debounced,
          siteName: currentSiteName,
        },
      ];
    }
    return mapped;
  }, [
    searchData,
    selectedProviders,
    siteProviders,
    isAdmin,
    debounced,
    currentSiteName,
  ]);

  const handleOptionSubmit = (value: string) => {
    if (value === ADD_NEW_VALUE) {
      // trigger external creation flow
      onAddNewProvider?.();
      setSearchValue('');
      return;
    }
    const prov =
      siteProviders?.find((siteProvider) => siteProvider.id === value) ??
      searchData.find((p) => p.id === value);
    if (prov && setSelectedProviders) {
      setSelectedProviders(prov);
    }
    setSearchValue('');
  };

  /**
   * A no-op options filter for the autocomplete component.
   * It simply returns the provided options as is. This is used to prevent the default filtering behavior.
   * @param {object} options - The options to filter.
   * @returns {object} The unfiltered options.
   */
  const optionsFilter: OptionsFilter = ({ options }: { options: any }): any => {
    return options;
  };

  return (
    <Autocomplete
      placeholder="Search for a provider"
      value={searchValue}
      onChange={setSearchValue}
      data={options}
      data-testid="add-provider-to-site-search"
      renderOption={(item) => {
        const { option } = item as any; // Mantine provides item.option
        if (option.isAddNew) {
          return (
            <Text size="sm">
              Create "
              <Text span fw={700}>
                {option.searchTerm}
              </Text>
              " and add to{' '}
              <Text span fw={700}>
                {option.siteName}?
              </Text>
            </Text>
          );
        }
        return <Text size="sm">{option.label}</Text>;
      }}
      onOptionSubmit={handleOptionSubmit}
      style={{ width: '100%' }}
      radius="sm"
      size="md"
      variant="filled"
      color={color}
      filter={optionsFilter}
      rightSection={
        <>
          {isLoading && (
            <Loader
              size={20}
              color={color}
              style={{ position: 'absolute', right: 15, top: 10 }}
            />
          )}
          {selectedProviders && (
            <IconX
              size={20}
              onClick={() => {
                if (setSelectedProviders) {
                  setSelectedProviders(null);
                  setSearchValue('');
                }
              }}
            />
          )}
        </>
      }
    />
  );
};
